// What each page tells search engines and social previews: title, description,
// indexing rule, canonical address, Open Graph / Twitter tags and structured
// data (Schema.org JSON-LD). Pure: the app (src/components/Seo.tsx) and the
// Cloudflare Worker (worker/index.ts, which puts the same tags into the HTML
// before it is sent) both use it, so they never disagree.
//
// Structured data only says what the site itself shows: no invented ratings,
// reviews or ratings. The address and support hours are the ones TradePrep
// publishes itself (src/…/core/business.ts).

import { type Site, SITES, siteFromHost } from '../../../supabase/functions/_shared/core/site';
import { addressText, BUSINESS, phonesText } from '../../../supabase/functions/_shared/core/business';
import { FAQS } from './faq';
import { type Guide, GUIDES, guideText } from '../guides';

export const BRAND = 'TradePrep';
export const DEFAULT_IMAGE_PATH = '/brand/og-image.png';
export const DEFAULT_IMAGE_SIZE = { width: 1200, height: 630 } as const;

/** Where the page is being served from (so links are absolute and match the site). */
export interface SeoContext {
  /** e.g. https://tradeprep.com.au */
  origin: string;
  site: Site;
}

export interface SeoLink {
  rel: 'canonical' | 'alternate';
  href: string;
  hreflang?: string;
}

export interface SeoMeta {
  title: string;
  description: string;
  /** `index` pages may appear in search; `noindex` ones may not. */
  index: boolean;
  /** The page's own path (for the canonical link), or null when it must not be indexed. */
  canonicalPath: string | null;
  /** Point at the same page on the other site (hreflang). */
  alternates: boolean;
  ogType: 'website' | 'product' | 'profile';
  /** A full address, or a path on this site. */
  image?: string;
  imageAlt?: string;
  jsonLd: Record<string, unknown>[];
  /** A plain-HTML version of the page's main content, for crawlers that don't run scripts (Worker only). */
  noscript?: { heading: string; text: string; links: { href: string; label: string }[] };
}

/** Collapse whitespace and cut at a word, with an ellipsis, so a description fits a search result (about 155 characters). */
export function truncate(text: string | null | undefined, max = 155): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–—-]+$/, '')}…`;
}

const absolute = (ctx: SeoContext, pathOrUrl: string) => (/^https?:\/\//.test(pathOrUrl) ? pathOrUrl : `${ctx.origin}${pathOrUrl}`);

// ---- Structured data building blocks ----

function organization(ctx: SeoContext) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${ctx.origin}/#organization`,
    name: BRAND,
    url: `${ctx.origin}/`,
    logo: `${ctx.origin}/icons/icon-512.png`,
    description: 'Practice questions and mock exams for people preparing for trade exams, with question banks written by training providers and tradespeople.',
    // Only the details TradePrep publishes itself: address, support email, phone numbers and hours.
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${BUSINESS.address.street}, ${BUSINESS.address.suburb}`,
      addressLocality: BUSINESS.address.city,
      addressCountry: BUSINESS.address.country,
    },
    contactPoint: BUSINESS.phones.map((p, i) => ({
      '@type': 'ContactPoint',
      contactType: 'customer support',
      telephone: p.e164,
      availableLanguage: 'English',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: BUSINESS.hours.days,
        opens: BUSINESS.hours.opens,
        closes: BUSINESS.hours.closes,
      },
      ...(i === 0 ? { email: ctx.site.supportEmail } : {}),
    })),
  };
}

function website(ctx: SeoContext) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${ctx.origin}/#website`,
    name: BRAND,
    url: `${ctx.origin}/`,
    inLanguage: ctx.site.locale,
    publisher: { '@id': `${ctx.origin}/#organization` },
    creator: { '@type': 'Organization', name: BUSINESS.builtBy.name, url: BUSINESS.builtBy.url },
  };
}

function breadcrumbs(ctx: SeoContext, trail: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.name, item: `${ctx.origin}${t.path}` })),
  };
}

function faqPage() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
  };
}

const NOSCRIPT_LINKS = (site: Site) => [
  { href: '/store', label: 'Browse question banks in the Store' },
  { href: '/register', label: 'Create a free account' },
  { href: '/guides', label: 'Study guides' },
  { href: '/about', label: 'About TradePrep' },
  { href: '/support', label: 'Help and support' },
  { href: '/terms', label: 'Terms of use' },
  { href: '/refunds', label: 'Refund policy' },
  { href: '/privacy', label: 'Privacy policy' },
  { href: `mailto:${site.supportEmail}`, label: site.supportEmail },
];

// ---- Fixed pages ----

export function homeMeta(ctx: SeoContext): SeoMeta {
  const description = 'Practise real-style trade exam questions, sit timed mock exams and see which topics to work on next. Question banks written by industry experts. Free to start.';
  return {
    title: `Trade Exam Practice Questions & Mock Exams | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    imageAlt: `${BRAND}: trade exam practice questions and mock exams`,
    jsonLd: [organization(ctx), website(ctx), faqPage()],
    noscript: {
      heading: 'Smash your trade exams',
      text: `${description} Study on your phone, with explanations for every answer.`,
      links: NOSCRIPT_LINKS(ctx.site),
    },
  };
}

export function storeMeta(ctx: SeoContext): SeoMeta {
  const description = 'Browse question banks and practice exams for electrical, plumbing, gasfitting, building and other trades. Try free samples before you buy.';
  return {
    title: `Trade Exam Question Banks & Practice Tests | ${BRAND} Store`,
    description,
    index: true,
    canonicalPath: '/store',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    imageAlt: `${BRAND} Store`,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Store', path: '/store' }])],
    noscript: { heading: `${BRAND} Store`, text: description, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function privacyMeta(ctx: SeoContext): SeoMeta {
  const description = 'How TradePrep collects, uses and protects your personal information, who we share it with, and your privacy rights in New Zealand and Australia.';
  return {
    title: `Privacy Policy | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/privacy',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Privacy policy', path: '/privacy' }])],
    noscript: { heading: 'Privacy policy', text: description, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function supportMeta(ctx: SeoContext): SeoMeta {
  const description = `Get help with your ${BRAND} account, payments, question content or becoming a provider. Email ${ctx.site.supportEmail} or call ${phonesText(ctx.site).split(' or ')[0]}.`;
  return {
    title: `Help & Support | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/support',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Help & support', path: '/support' }])],
    noscript: { heading: 'Help & support', text: description, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function termsMeta(ctx: SeoContext): SeoMeta {
  const description = `The terms for using ${BRAND}: accounts, buying study material, training providers, acceptable use and your consumer rights in New Zealand and Australia.`;
  return {
    title: `Terms of Use | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/terms',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Terms of use', path: '/terms' }])],
    noscript: { heading: 'Terms of use', text: description, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function refundsMeta(ctx: SeoContext): SeoMeta {
  const description = `How refunds work on ${BRAND}: what you can get your money back for, how to ask, and how subscriptions are cancelled. Your consumer rights always apply.`;
  return {
    title: `Refund Policy | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/refunds',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Refund policy', path: '/refunds' }])],
    noscript: { heading: 'Refund policy', text: description, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function aboutMeta(ctx: SeoContext): SeoMeta {
  const description = `The story behind ${BRAND}: an idea from Flynt, a Christchurch electrician, built into a trade exam practice platform by Aaron at ${BUSINESS.builtBy.name}.`;
  return {
    title: `About Us | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/about',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])],
    noscript: { heading: `About ${BRAND}`, text: `${description} Based at ${addressText()}.`, links: NOSCRIPT_LINKS(ctx.site) },
  };
}

export function guidesMeta(ctx: SeoContext): SeoMeta {
  const description = 'Free, practical study guides for trade exams: how to study, get the most from practice questions and mock exams, plan your weeks and prepare for exam day.';
  return {
    title: `Trade Exam Study Guides | ${BRAND}`,
    description,
    index: true,
    canonicalPath: '/guides',
    alternates: true,
    ogType: 'website',
    image: DEFAULT_IMAGE_PATH,
    jsonLd: [breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Study guides', path: '/guides' }])],
    noscript: {
      heading: 'Study guides',
      text: `${description} ${GUIDES.map((g) => g.title).join('. ')}.`,
      links: [...GUIDES.map((g) => ({ href: `/guides/${g.slug}`, label: g.title })), ...NOSCRIPT_LINKS(ctx.site)],
    },
  };
}

export function guideMeta(ctx: SeoContext, g: Guide): SeoMeta {
  const path = `/guides/${g.slug}`;
  return {
    title: `${g.title} | ${BRAND}`,
    description: g.description,
    index: true,
    canonicalPath: path,
    alternates: true,
    ogType: 'website',
    image: g.image?.src ?? DEFAULT_IMAGE_PATH,
    imageAlt: g.image?.alt,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: g.title,
        description: g.description,
        dateModified: g.updated,
        ...(g.image ? { image: absolute(ctx, g.image.src) } : {}),
        inLanguage: ctx.site.locale,
        mainEntityOfPage: `${ctx.origin}${path}`,
        author: { '@type': 'Organization', name: BRAND, url: `${ctx.origin}/` },
        publisher: { '@id': `${ctx.origin}/#organization` },
      },
      breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Study guides', path: '/guides' }, { name: g.title, path }]),
    ],
    noscript: {
      heading: g.title,
      text: guideText(g),
      links: [{ href: '/guides', label: 'More study guides' }, ...NOSCRIPT_LINKS(ctx.site).filter((l) => l.href !== '/guides')],
    },
  };
}

/** Pages that must stay out of search results: sign-in, store search, the app itself, addresses that aren't pages. */
export function noindexMeta(title: string, description = 'Exam practice for apprentices.'): SeoMeta {
  return { title: `${title} | ${BRAND}`, description, index: false, canonicalPath: null, alternates: false, ogType: 'website', jsonLd: [] };
}

export const authTitles: Record<string, string> = {
  '/login': 'Sign in',
  '/register': 'Create your free account',
  '/forgot-password': 'Reset your password',
  '/reset-password': 'Choose a new password',
  '/unsubscribe': 'Unsubscribe',
};

export const notFoundMeta = (): SeoMeta => noindexMeta('Page not found');

// ---- Store products and providers ----

/** The parts of a Store product search engines are told about. Matches the `seo_product` and `get_store_product` results. */
export interface SeoProduct {
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image_url?: string | null;
  product_type?: string | null;
  price_cents: number;
  sale_price_cents?: number | null;
  currency: string;
  sale_mode?: string | null;
  subscription_price_cents?: number | null;
  question_count?: number | null;
  rating_avg?: number | null;
  rating_count?: number | null;
  is_demo?: boolean | null;
  provider?: { display_name: string; slug?: string | null } | null;
  category?: { name: string } | null;
  industry?: { name: string } | null;
}

export interface SeoProvider {
  slug: string;
  display_name: string;
  description?: string | null;
  avatar_url?: string | null;
  website?: string | null;
  product_count?: number | null;
}

const money = (cents: number) => (cents / 100).toFixed(2);

function productOffers(p: SeoProduct, url: string) {
  const offers: Record<string, unknown>[] = [];
  const mode = p.sale_mode ?? 'one_off';
  if (mode !== 'subscription') {
    offers.push({ '@type': 'Offer', url, price: money(p.sale_price_cents ?? p.price_cents), priceCurrency: p.currency, availability: 'https://schema.org/InStock' });
  }
  if ((mode === 'subscription' || mode === 'both') && p.subscription_price_cents) {
    offers.push({
      '@type': 'Offer',
      url,
      price: money(p.subscription_price_cents),
      priceCurrency: p.currency,
      availability: 'https://schema.org/InStock',
      priceSpecification: { '@type': 'UnitPriceSpecification', price: money(p.subscription_price_cents), priceCurrency: p.currency, unitCode: 'MON' },
    });
  }
  return offers;
}

export function productMeta(ctx: SeoContext, p: SeoProduct): SeoMeta {
  const path = `/store/${p.slug}`;
  const url = `${ctx.origin}${path}`;
  const by = p.provider?.display_name ?? BRAND;
  const questions = p.question_count ? `${p.question_count.toLocaleString('en-NZ')} questions` : '';
  const fallback = [p.subtitle, questions && `${questions} by ${by}`].filter(Boolean).join('. ');
  const description = truncate(p.description || fallback || `${p.title}: practice questions and mock exams by ${by} on ${BRAND}.`);
  const image = p.image_url || DEFAULT_IMAGE_PATH;
  const offers = productOffers(p, url);
  return {
    title: `${p.title} | ${BRAND}`,
    description,
    // Demo content isn't for search engines.
    index: !p.is_demo,
    canonicalPath: p.is_demo ? null : path,
    alternates: !p.is_demo,
    ogType: 'product',
    image,
    imageAlt: p.title,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: p.title,
        description,
        url,
        sku: p.slug,
        ...(p.image_url ? { image: p.image_url } : {}),
        brand: { '@type': 'Brand', name: by },
        ...(p.category?.name || p.industry?.name ? { category: p.category?.name ?? p.industry?.name } : {}),
        ...(offers.length ? { offers: offers.length === 1 ? offers[0] : offers } : {}),
        // Only when real reviews exist (they're shown on the page too).
        ...((p.rating_count ?? 0) > 0 && p.rating_avg ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: p.rating_avg, reviewCount: p.rating_count } } : {}),
      },
      breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Store', path: '/store' }, { name: p.title, path }]),
    ],
    noscript: {
      heading: p.title,
      text: [description, p.provider?.display_name ? `By ${p.provider.display_name}.` : ''].filter(Boolean).join(' '),
      links: [{ href: '/store', label: 'More question banks in the Store' }, ...NOSCRIPT_LINKS(ctx.site).slice(1)],
    },
  };
}

export function providerMeta(ctx: SeoContext, p: SeoProvider): SeoMeta {
  const path = `/store/providers/${p.slug}`;
  const url = `${ctx.origin}${path}`;
  const courses = p.product_count ? `${p.product_count} question bank${p.product_count === 1 ? '' : 's'}` : 'Question banks';
  // A short or missing profile text is filled out so the description says what's here.
  const about = (p.description ?? '').trim();
  const description = truncate(about.length >= 70 ? about : `${about ? `${about.replace(/\.?$/, '.')} ` : ''}${courses} and practice exams by ${p.display_name} on ${BRAND}.`);
  return {
    title: `${p.display_name}: Question Banks & Practice Exams | ${BRAND}`,
    description,
    index: true,
    canonicalPath: path,
    alternates: true,
    ogType: 'profile',
    image: p.avatar_url || DEFAULT_IMAGE_PATH,
    imageAlt: p.display_name,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: p.display_name,
        url,
        ...(p.description ? { description: truncate(p.description, 300) } : {}),
        ...(p.avatar_url ? { logo: p.avatar_url } : {}),
        ...(p.website ? { sameAs: [p.website] } : {}),
      },
      breadcrumbs(ctx, [{ name: 'Home', path: '/' }, { name: 'Store', path: '/store' }, { name: p.display_name, path }]),
    ],
    noscript: { heading: p.display_name, text: description, links: [{ href: '/store', label: 'Browse the Store' }, ...NOSCRIPT_LINKS(ctx.site).slice(1)] },
  };
}

// ---- Links: canonical and hreflang ----

/**
 * The links that name the page itself as the real one (canonical) and point at
 * its twin on the other site (hreflang), so the New Zealand and Australian
 * sites aren't treated as two copies of one. Alternates only on addresses that
 * are one of the two sites (not previews or local development).
 */
export function seoLinks(ctx: SeoContext, meta: SeoMeta): SeoLink[] {
  if (!meta.index || meta.canonicalPath === null) return [];
  const path = meta.canonicalPath;
  const links: SeoLink[] = [{ rel: 'canonical', href: `${ctx.origin}${path}` }];
  let host = '';
  try {
    host = new URL(ctx.origin).hostname;
  } catch {
    /* no alternates */
  }
  if (meta.alternates && siteFromHost(host)) {
    for (const site of Object.values(SITES)) links.push({ rel: 'alternate', hreflang: site.locale, href: `https://${site.domain}${path}` });
    links.push({ rel: 'alternate', hreflang: 'x-default', href: `https://${SITES.nz.domain}${path}` });
  }
  return links;
}

/** Every Open Graph and Twitter tag for a page, as name/property → content. */
export function socialTags(ctx: SeoContext, meta: SeoMeta): { property?: string; name?: string; content: string }[] {
  const url = meta.canonicalPath !== null ? `${ctx.origin}${meta.canonicalPath}` : undefined;
  const image = meta.image ? absolute(ctx, meta.image) : undefined;
  const isDefault = image?.endsWith(DEFAULT_IMAGE_PATH);
  const otherLocale = Object.values(SITES).find((s) => s.code !== ctx.site.code)!.locale;
  const tags: { property?: string; name?: string; content: string }[] = [
    { property: 'og:site_name', content: BRAND },
    { property: 'og:type', content: meta.ogType },
    { property: 'og:title', content: meta.title },
    { property: 'og:description', content: meta.description },
    { property: 'og:locale', content: ctx.site.locale.replace('-', '_') },
    { property: 'og:locale:alternate', content: otherLocale.replace('-', '_') },
  ];
  if (url) tags.push({ property: 'og:url', content: url });
  if (image) {
    tags.push({ property: 'og:image', content: image });
    if (isDefault) tags.push({ property: 'og:image:width', content: String(DEFAULT_IMAGE_SIZE.width) }, { property: 'og:image:height', content: String(DEFAULT_IMAGE_SIZE.height) });
    if (meta.imageAlt) tags.push({ property: 'og:image:alt', content: meta.imageAlt });
  }
  tags.push(
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: meta.title },
    { name: 'twitter:description', content: meta.description },
  );
  if (image) tags.push({ name: 'twitter:image', content: image });
  return tags;
}

/** The right Site for an address (www and subdomains included); anything else is the default site. */
export function contextForOrigin(origin: string): SeoContext {
  let host = '';
  try {
    host = new URL(origin).hostname;
  } catch {
    /* default */
  }
  const code = siteFromHost(host);
  return { origin, site: SITES[code ?? 'nz'] };
}
